import Vue from "vue";
import obj2fd from "obj2fd";

// state
const initialState = {
    init: false,
    loading: true,
    submitted: false,
    service: null,
    step: 0,
    output: [],
    places: [],
    documentTypes: [],
    clients: [],
    is_client_intermediary: false,
    client: null,
    sites: [],
    types: [],
    templates: [],
    template: null,
    towing_registration: "",
    trailer_registration: "",
    site: null,
    type: null,
    customs_type: null,
    reference: "",
    notes: "",
    // If add new services
    // do not forget to update services getters in this store

    services: {
        a846: false,
        dsce: false,
        dsce_by: "client",
        cts_service: null,
        documentary_control: false,
    },
    data: {
        animals: "",
        incoterm: "",
        transport_cost: "",
        crossing_date: "",
        crossing_place: null,
        crossing_date_use_today: false,
    },
    documents: [],
};

const state = _.cloneDeep(initialState);
const initialServices = _.cloneDeep(initialState.services);

// getters
const getters = {
    truck(state) {
        return state.towing_registration + " - " + state.trailer_registration;
    },

    service(state) {
        return state.service
            ? i18n.t("account/bookings.services." + state.service)
            : "-";
    },

    place(state) {
        return _.get(
            _.find(state.places, (p) => p.value == state.data.crossing_place),
            "label",
            "-"
        );
    },

    type(state) {
        var type = _.get(
            _.find(state.types, (t) => t.key == state.type),
            "label",
            "-"
        );

        return state.type == "animals"
            ? type + " - " + i18n.t("app.animals." + state.data.animals)
            : type;
    },

    services(state) {
        var output = [];

        _.each(state.services, (value, key) => {
            if (value) {
                var line = null;

                switch (key) {
                    case "a846":
                        line = i18n.t("account/bookings.846A");
                        break;

                    case "dsce":
                        line = i18n.t("account/bookings.CHED");
                        line += " - ";
                        line +=
                            state.services.dsce_by == "client"
                                ? i18n.t("account/bookings.I do it myself")
                                : i18n.t("account/bookings.Do it for me");
                        break;

                    case "cts_service":
                        line =
                            value == "simple"
                                ? i18n.t("account/bookings.Simple service")
                                : value == "complete"
                                ? i18n.t("account/bookings.Complete service")
                                : i18n.t("account/bookings.Clearance transit");
                        break;

                    case "documentary_control":
                        line = i18n.t("account/bookings.Documentary control");
                        break;
                }

                if (line) {
                    output.push(line);
                }
            }
        });

        return output;
    },

    templateData(state) {
        return {
            service: state.service,
            services: state.services,
            site: state.site,
            type: state.type,
            customs_type: state.customs_type,
            data: {
                animals: state.data.animals,
                crossing_date_use_today: state.data.crossing_date_use_today,
                crossing_place: state.data.crossing_place,
            },
        };
    },

    progress(state) {
        var keys = [
            "service",
            "towing_registration",
            "trailer_registration",
            "type",
            "customs_type",
            "reference",
            "data.crossing_date",
            "data.crossing_place",
            "documents",
        ];

        if (["customs", "customs-sivep"].includes(state.service)) {
            keys.push("data.incoterm", "data.transport_cost");
        }

        if (state.type === "animals") {
            keys.push("data.animals");
        }

        var total = keys.length;

        let count = keys.reduce((acc, key) => {
            const value = _.get(state, key);

            if (_.isArray(value) ? value.length : value) {
                return acc + 1;
            }

            if (
                key === "documents" &&
                !value.length &&
                state.service === "parking" &&
                ["complete", "simple"].includes(state.services.cts_service)
            ) {
                return acc + 1;
            }

            return acc;
        }, 0);

        return Math.floor((count * 100) / total);
    },

    mandate() {
        return state.client && state.client.representation_mandate
            ? state.client.representation_mandate.type.key
            : null;
    },
};

// actions
const actions = {
    applyTemplate({ state, commit }, template) {
        commit("setTemplate", template);
        Object.assign(state, template.data);

        if(template.data.data.crossing_date_use_today) {
            commit("setData", {
                key: "crossing_date",
                value: new Date()
            })
        }
    },

    initBooking({ state, commit }) {
        commit("setLoading", true);
        commit("resetState");

        return new Promise((resolve, reject) => {
            axios
                .post("/account/bookings/data")
                .then((response) => {
                    commit("setDocumentTypes", response.data.documentTypes);
                    commit("setTemplates", response.data.templates);
                    commit("setTypes", response.data.types);
                    commit("setPlaces", response.data.places);
                    commit("setSites", response.data.sites);
                    commit("setLoading", false);
                    commit("setInit", true);
                    resolve();
                })
                .catch((error) => {
                    reject();
                });
        });
    },

    saveBooking({ state, commit }) {
        return new Promise((resolve, reject) => {
            // Clean data
            var data = _.cloneDeep(state);
            delete data.init;
            delete data.loading;
            delete data.submitted;
            delete data.step;
            delete data.places;
            delete data.documentTypes;
            delete data.sites;
            delete data.types;
            delete data.templates;
            data.clients = data.clients.map((client) => {
                return {
                    id: client.id,
                    type: client.type,
                };
            });
            if (data.client && data.client.id) {
                data.client = data.client.id;
            }

            axios
                .post("/account/bookings/store", obj2fd(data))
                .then((response) => {
                    commit("setOutput", response.data);
                    commit("setStep", 4);
                    commit("setSubmitted", true);
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
};

// mutations
const mutations = {
    setInit(state, value) {
        state.init = value;
    },

    setLoading(state, value) {
        state.loading = value;
    },

    setSubmitted(state, value) {
        state.submitted = value;
    },

    setService(state, value) {
        state.service = value;
    },

    setStep(state, value) {
        state.step = value;
    },

    setOutput(state, value) {
        state.output = value;
    },

    setPlaces(state, value) {
        state.places = value;
    },

    setDocumentTypes(state, value) {
        state.documentTypes = value;
    },

    setSites(state, value) {
        state.sites = value;
    },

    setTypes(state, value) {
        state.types = value;
    },

    setTemplates(state, value) {
        state.templates = value;
    },

    setTemplate(state, value) {
        state.template = value;
    },

    setTowingRegistration(state, value) {
        state.towing_registration = value;
    },

    setTrailerRegistration(state, value) {
        state.trailer_registration = value;
    },

    setSite(state, value) {
        state.site = value;
    },

    setType(state, value) {
        state.type = value;
    },

    setCustomsType(state, value) {
        state.customs_type = value;
    },

    setReference(state, value) {
        state.reference = value;
    },

    setNotes(state, value) {
        state.notes = value;
    },

    setServices(state, payload) {
        state.services[payload.key] = payload.value;
    },

    setData(state, payload) {
        Vue.set(state.data, payload.key, payload.value);
    },

    setDocuments(state, value) {
        state.documents = value;
    },

    resetState(state) {
        Object.assign(state, initialState);
    },

    resetServices(state) {
        Object.assign(state.services, initialServices);
    },

    setClients(state, value) {
        state.clients = value;
    },

    setClientIntermediary(state, value) {
        state.is_client_intermediary = value;
    },

    setClient(state, value) {
        state.client = value;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
